import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { isAxiosError } from "axios";
import { VideoData } from "../../../shared/interfaces/VideoData";
import { transcribeVideo } from "../../../shared/api/transcriptsApi";

/** Outcome of transcribing a single video, surfaced in the "choose to view" dialog. */
export interface TranscriptionResult {
  video: VideoData;
  ok: boolean;
  /** Server `{ message }` (or a fallback) when the transcription failed. */
  message?: string;
}

const FALLBACK_ERROR =
  "An error occurred while transcribing this video. Please try again later.";

/**
 * Drives the "Transcribe video(s)" flow on the Generate Report page.
 *
 * - `transcribeVideo` submits an async job and polls until the transcript is
 *   ready (see {@link transcribeVideo}).
 * - A single video transcribes and redirects straight to its transcript editor.
 * - Multiple videos are transcribed in parallel; the user then picks which
 *   transcript to open. Per-video failures are surfaced individually and never
 *   sink the batch (`Promise.allSettled`).
 */
export const useTranscription = (
  videoDataList: VideoData[],
  videoData: VideoData | null,
  showError: (message: string) => void,
  showInfo: (message: string) => void,
) => {
  const navigate = useNavigate();
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [selectOpen, setSelectOpen] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [results, setResults] = useState<TranscriptionResult[]>([]);

  // Aborts in-flight transcription polling on unmount so a resolved/failed job
  // can't update state or navigate after the page is gone.
  const abortRef = useRef<AbortController | null>(null);
  useEffect(() => {
    const controller = new AbortController();
    abortRef.current = controller;
    return () => controller.abort();
  }, []);

  const goToTranscript = useCallback(
    (evidenceId: string) => {
      navigate(`/transcripts/${evidenceId}`);
    },
    [navigate],
  );

  const startTranscription = useCallback(
    async (videos: VideoData[]) => {
      if (isTranscribing || videos.length === 0) return;
      setSelectOpen(false);
      setIsTranscribing(true);

      showInfo(
        `Transcription started for ${
          videos.length === 1 ? "this video" : `${videos.length} videos`
        }. This may take a few moments. It is safe to navigate away from this page while the transcription is in progress.`,
      );

      const signal = abortRef.current?.signal;

      const settled = await Promise.allSettled(
        videos.map((video) => transcribeVideo(video.id, signal)),
      );

      // Hook/page unmounted mid-flight — drop the result without touching state
      // or navigating.
      if (signal?.aborted) return;

      const nextResults: TranscriptionResult[] = settled.map((outcome, i) => {
        const video = videos[i];
        if (outcome.status === "fulfilled") {
          return { video, ok: true };
        }
        const error = outcome.reason;
        let message = FALLBACK_ERROR;
        if (isAxiosError(error)) {
          message =
            (error.response?.data as { message?: string } | undefined)?.message ??
            FALLBACK_ERROR;
        } else if (error instanceof Error && error.message) {
          message = error.message;
        }
        return { video, ok: false, message };
      });

      setIsTranscribing(false);

      // Single video: skip the picker and jump straight into the editor on
      // success, otherwise surface the error inline.
      if (nextResults.length === 1) {
        const only = nextResults[0];
        if (only.ok) {
          goToTranscript(only.video.id);
        } else {
          showError(only.message ?? FALLBACK_ERROR);
        }
        return;
      }

      // Batch: let the user choose which transcript to open.
      setResults(nextResults);
      setViewOpen(true);
    },
    [isTranscribing, goToTranscript, showError, showInfo],
  );

  const handleTranscribeClick = useCallback(() => {
    if (isTranscribing) return;
    if (videoDataList.length <= 1) {
      const single = videoData ?? videoDataList[0];
      // No videoUrl means no file in S3 yet — transcription can only fail
      // server-side, so don't submit it (the button is hidden in this state;
      // this is the belt to that suspender).
      if (!single?.videoUrl) return;
      void startTranscription([single]);
      return;
    }
    setSelectOpen(true);
  }, [isTranscribing, videoDataList, videoData, startTranscription]);

  const closeSelect = useCallback(() => setSelectOpen(false), []);
  const closeView = useCallback(() => setViewOpen(false), []);

  return {
    isTranscribing,
    selectOpen,
    viewOpen,
    results,
    handleTranscribeClick,
    startTranscription,
    goToTranscript,
    closeSelect,
    closeView,
  };
};
